<template>
  <div role="dialog" class="popup fadein absolute z-50 flex justify-center items-center">
    <div class="overflow-y-auto popup-wrapper z-50 w-1/2 xl:w-1/3 max-w-lg flex flex-row-reverse rounded-xl bg-slate-middle max-h-screen">
      <ConfirmationPopup
        v-if="state.confirmationPopupIsVisible"
        :text="t('deletionwarning')"
        @confirm="deleteWall()"
        @close-popup="closeDeletionPopup()"
      />
      <ConfirmationPopup
        v-if="state.duplicationPopupIsVisible"
        :text="t('duplicationwarning')"
        @confirm="saveAndClose(true)"
        @close-popup="closeDuplicationPopup()"
      />
      <div
        class="absolute h-6 w-6 mr-2 mt-3 p-1.5 bg-black rounded-full flex justify-center items-center cursor-pointer"
        tabindex="0"
        ref="firstFocus"
        @pointerdown.stop="close()"
        @keydown.enter="close()"
      >
        <img class="w-5 h-5 invert rotate-45" :src="Close" />
      </div>

      <div class="flex flex-col items-start w-full p-6 px-8 overflow-y-auto">
        <div class="bg-slate-black px-4 py-4 flex flex-col justify-start items-start rounded-md w-full mt-4">
          <input
            v-model="state.wallName"
            tabindex="0"
            class="text-xl font-bold w-full bg-slate-dark p-4 rounded-lg text-slate-white"
            type="text"
            id="wall-edit-name"
          />
          <div class="flex items-center mt-5 mb-2">
            <div
              v-for="(subject, index2) in state.subjects.slice(0, 3)"
              :key="index2"
              class="flex items-center mr-2 rounded-2xl"
              :style="`background-color: ${colorStore.actions.selectColor(subject.name).background};`"
            >
              <span v-if="subject.icon" class="mx-1.5 flex justify-center">{{ subject.icon }}</span>
              <span class="mr-3" :style="`color: ${colorStore.actions.selectColor(subject.name).text};`">{{ subject.name }}</span>
            </div>
            <p
              v-if="wall && wall.details.subjects.length - 3 > 0"
              class="bg-raster text-slate-bright p-1 rounded-2xl w-6 h-6 flex justify-center items-center text-sm"
            >
              {{ `+${wall.details.subjects.length - 3}` }}
            </p>
            <p v-if="wall && wall.details.subjects.length === 0" class="bg-raster flex items-center rounded-2xl text-white px-2 py-px text-sm">
              {{ t('nosubjects') }}
            </p>
          </div>
          <div class="flex flex-col mb-5 pl-2">
            <div class="py-1 flex flex-row items-center">
              <p class="text-slate-bright py-1 mr-4">{{ t('owner') }}</p>
              <div
                class="px-1 rounded-lg"
                :style="`background-color: ${colorStore.actions.selectColor(wall ? wall.users.owner.username : (user?.profile.username ?? '')).background};`"
              >
                <p
                  class="font-bold text-left"
                  :style="`color: ${colorStore.actions.selectColor(wall ? wall.users.owner.username : (user?.profile.username ?? '')).text};`"
                >
                  {{ wall ? wall.users.owner.username : (user?.profile.username ?? '') }}
                </p>
              </div>
            </div>
            <div class="flex flex-row">
              <p class="text-slate-bright py-1 mr-4">{{ t('created') }}</p>
              <p class="text-slate-white font-bold text-left py-1">
                {{ wall ? wall.status.created.toLocaleDateString('nb-NO') : new Date().toLocaleDateString('nb-NO') }}
              </p>
            </div>
          </div>
          <textarea
            id="wall-edit-description"
            v-model="state.wallDescription"
            class="w-full text-slate-white text-md bg-slate-dark p-4 rounded-lg resize-none overflow-auto overflow-x-hidden mb-2"
            @pointerup="removeDefaultText()"
            :placeholder="t('description')"
          >
          </textarea>
        </div>

        <!-- Oppgaver -->
        <div class="bg-slate-black px-4 py-4 flex flex-col justify-start items-start rounded-md w-full mt-4">
          <p class="text-slate-white font-bold text-xl">{{ t('task') }}</p>
          <p class="py-2 text-slate-white text-sm text-left">{{ t('oppgaveDescription') }}</p>
          <div class="pb-3 flex items-start flex-col w-full max-h-80 overflow-scroll overflow-x-hidden p-px rounded-xl">
            <div v-for="(task, index) in state.tasks" :key="index" class="w-full flex px-2 py-1 my-1 items-center rounded-l-lg bg-slate-dark">
              <input
                class="w-full p-3 font-middle text-slate-bright text-md bg-slate-dark focus:rounded-lg z-50 resize-none overflow-y-scroll overflow-x-hidden h-14"
                type="text"
                :id="`task-name-${index}`"
                :value="task.title"
                @keyup="submitTaskEdit(index, $event.target)"
                @click="removeTaskDefaultText(task)"
                @touchstart="removeTaskDefaultText(task)"
              />
              <div class="bg-slate-dark rounded-r-lg">
                <Toggle
                  :value="state.tasks[index].allowBoardCreation"
                  @toggle-value="($event: any) => toggleBoardCreation(index, $event)"
                  colour="green"
                  :message="{ on: t('all'), off: t('onlyGroup') }"
                />
              </div>
              <div v-if="state.tasks.length > 1" class="task-controls bg-slate-dark p-4 pr-1 rounded-r-lg h-14 flex justify-end items-center">
                <div
                  v-if="index > 0"
                  class="bg-slate-middle rounded-full z-50 text-white w-8 h-8 ml-1 flex justify-center items-center cursor-pointer"
                  @pointerdown.stop="submitTaskOrderChange(index, -1)"
                  @keydown.enter="submitTaskOrderChange(index, -1)"
                >
                  <img class="w-2.5 rotate-90 invert" :src="Arrow" />
                </div>
                <div v-if="index == 0" class="z-50 text-white w-8 h-8 ml-1 flex justify-center items-center cursor-pointer"></div>
                <div
                  v-if="index + 1 < state.tasks.length"
                  class="bg-slate-middle rounded-full z-50 text-white w-8 h-8 ml-1 flex justify-center items-center cursor-pointer"
                  @pointerdown.stop="submitTaskOrderChange(index, 1)"
                  @keydown.enter="submitTaskOrderChange(index, 1)"
                >
                  <img class="w-2.5 mt-1 -rotate-90 invert" :src="Arrow" />
                </div>
                <div
                  v-if="index == state.tasks.length - 1"
                  class="z-50 text-white w-8 h-8 ml-1 flex justify-center items-center cursor-pointer"
                ></div>
                
                <div class="relative group">
  <!-- Tooltip: visible when hovering the button -->
  

  <!-- Delete button -->
  

<!-- Delete button + hover handlers -->
<div
  :class="[
    deleteButtonStyles(taskDeletionIsAllowed(index)),
    'red-btn rounded-full text-white w-8 h-8 ml-3 flex justify-center items-center',
    !taskDeletionIsAllowed(index) ? 'cursor-not-allowed' : 'cursor-pointer'
  ]"
  @mouseenter="!taskDeletionIsAllowed(index) && showTaskTooltip($event, index)"
  @mouseleave="hideTaskTooltip"
  @pointerdown.stop="submitTaskDeletion(index)"
  @keydown.enter="submitTaskDeletion(index)"
>
  <img class="w-3 rotate-45 invert" :src="Plus" />
</div>

</div>

              </div>
            </div>
            <div class="flex items-center font-middle p-4 rounded-lg mt-2 justify-between h-14">
              <p class="text-slate-white text-md cursor-default">{{ t('addtask') }}</p>
              <div
                class="group ml-4 rounded-full bg-slate-middle regular-btn hover:bg-tw-orange-bright w-12 h-12 flex justify-center items-center cursor-pointer"
                @pointerdown.stop="addTask()"
                @keydown.enter="addTask()"
              >
                <img class="w-3 h-3 invert group-hover:invert-0" :src="Plus" />
              </div>
            </div>
          </div>
        </div>

        <!-- Subject-->
        <div class="bg-slate-black px-4 py-4 my-5 flex flex-col justify-start items-start rounded-md w-full mt-4">
          <p class="text-slate-white font-bold text-xl mb-2">{{ t('belongsTo') }}</p>
          <div class="flex w-full cursor-pointer mt-3 justify-center items-center">
            <input
              v-model="state.searchSubject"
              id="subject-search"
              class="flex-grow text-green-400 text-md bg-slate-dark h-12 px-4 rounded-lg resize-none overflow-scroll overflow-x-hidden z-51"
              :placeholder="t('search')"
              @keyup="setDropdownState()"
            />
            <div
              class="flex h-10 w-10 items-center justify-center"
              @pointerdown.stop="state.subjectDropdownIsVisible = !state.subjectDropdownIsVisible"
              @keydown.enter="state.subjectDropdownIsVisible = !state.subjectDropdownIsVisible"
            >
              <img id="subject-search-dropdown" class="relative -rotate-90 w-3 invert" :src="Arrow" />
            </div>
          </div>
          <div
            v-if="state.subjectDropdownIsVisible"
            class="bg-slate-black border border-gray-700 border-t-0 p-3 pt-4 w-full rounded-b-lg max-h-56 overflow-scroll overflow-x-hidden"
          >
            <div
              v-for="(subject, index) in filteredSubjects"
              :key="index"
              class="flex justify-between items-center cursor-pointer text-left py-px my-2 mb-4"
            >
              <div class="flex px-1 rounded-lg" :style="`background-color: ${colorStore.actions.selectColor(subject.name).background};`">
                <span v-if="subject.icon" class="mr-2">{{ subject.icon }}</span>
                <p class="text-md text-slate-white font-bold" :style="`color: ${colorStore.actions.selectColor(subject.name).text};`">
                  {{ subject.name }}
                </p>
              </div>
              <div v-if="subjectIsSelected(subject)" class="flex items-center">
                <p class="text-slate-bright text-sm">{{ t('alreadychosen') }}</p>
                <div
                  class="red-btn rounded-full z-51 text-white w-6 h-6 ml-3 flex justify-center items-center cursor-pointer"
                  @pointerdown.stop="submitSubjectRemoval(subject)"
                  @keydown.enter="submitSubjectRemoval(subject)"
                >
                  <img class="w-3 rotate-45 invert" :src="Plus" />
                </div>
              </div>
              <div v-else class="flex items-center">
                <div class="bg-blue-middle rounded-full z-51 text-white w-6 h-6 ml-3 flex justify-center items-center cursor-pointer">
                  <img class="w-3 invert" :src="Plus" @pointerdown.stop="addSubject(subject)" @keydown.enter="addSubject(subject)" />
                </div>
              </div>
            </div>
            <div class="bg-slate-black rounded-xl flex justify-between items-center" :class="subjectWrapperClass()">
              <div class="flex items-center p-1 w-full">
                <div v-if="state.selectedEmoji" class="w-6 h-6 ml-4 mr-2 rounded-full">
                  {{ state.selectedEmoji }}
                </div>
                <input
                  v-model="state.newSubject"
                  maxlength="15"
                  class="w-full text-slate-bright bg-slate-dark rounded-lg text-md my-2 p-1 mx-2 px-4 resize-none overflow-scroll overflow-x-hidden z-51"
                  type="text"
                  :placeholder="t('addsubject')"
                />
                <button
                  v-if="state.newSubject.length > 0 && state.newSubject !== t('addsubject') && state.selectedEmoji"
                  class="bg-blue-middle p-1 px-3 rounded-full flex justify-center items-center mx-1 cursor-pointer text-white whitespace-nowrap"
                  @click="confirmNewSubject()"
                >
                  {{ t('add') }}
                </button>
              </div>
              <button
                class="regular-btn flex justify-center items-center mr-3 p-1 px-3 rounded-full text-white cursor-pointer whitespace-nowrap"
                @click="toggleEmojiDropdown()"
              >
                {{ t('addicon') }}
              </button>
            </div>
            <div v-if="state.emojiDropdownIsVisible" class="-mt-5 flex flex-wrap bg-slate-bright p-3 rounded-xl justify-evenly">
              <button v-for="(emoji, index) in emojis" :key="index" class="p-2 cursor-pointer emoji" @click="chooseEmoji(emoji)">{{ emoji }}</button>
            </div>
          </div>
        </div>

        <div v-if="user && pageIsDashboard && wall" class="pt-5 flex w-full mb-8">
          <button
            v-if="common.actions.hasMinimumPermission(user, PermissionLevel.Copy, props.wall)"
            class="regular-btn rounded-3xl cursor-pointer p-3 px-5 text-md text-slate-white mr-2 capitalize w-1/2"
            @click="invokeDuplicationPopup()"
          >
            {{ t('duplicate') }}
          </button>
          <button class="red-btn cursor-pointer p-3 px-5 text-md text-slate-white ml-2 w-1/2" @click="invokeDeletionPopup()">
            {{ t('delete') }}
          </button>
        </div>
        <!--div class="h-px bg-slate-bright my-5 w-full"></div-->
        <div class="flex justify-between w-full">
          <button class="gray-btn cursor-pointer p-3 px-5 text-md text-slate-white capitalize" @click="close()">{{ t('cancel') }}</button>
          <button class="blue-btn cursor-pointer p-3 px-5 text-md text-slate-white" @click="saveAndClose(false)">{{ t('save') }}</button>
        </div>
      </div>
    </div>
  </div>



  <teleport to="body">
  <div
    v-if="hover.show && !taskDeletionIsAllowed(hover.index)"
    class="fixed z-[9999] bg-red-700/90 text-white text-xs px-2 py-1 rounded-md shadow-md whitespace-nowrap pointer-events-none"
    :style="{ top: hover.top + 'px', left: hover.left + 'px' }"
  >
    {{ t('taskHasContent') }}
  </div>
  </teleport>
</template>

<script setup lang="ts">
  import { onMounted, PropType, computed, onBeforeMount, ref, nextTick } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { Wall, Task, DialogMessageType, Note, Parcel, Board, BoardEntry, Subject, IWall, Sharing, ISharing } from '@/common/classes'
  import { ParcelType, emojis, PermissionLevel, CreatorType, GroupType } from '@/constants'

  import useWallStore from '@/composables/wallStore'
  import { useNoteStore } from '@/composables/noteStore'
  import { useUserStore } from '@/composables/userStore'

  import useCommonStore from '@/composables/common'
  import useParcelStore from '@/composables/parcel'
  import useColorStore from '@/composables/colors'
  import useDialogStore from '@/composables/dialog'

  import Close from './../../assets/icons/fontawesome/plus-solid.svg'
  import Plus from './../../assets/icons/fontawesome/plus-solid.svg'
  import Arrow from '../../assets/icons/fontawesome/angle-left-solid.svg'
  import ConfirmationPopup from './ConfirmationPopup.vue'
  import Toggle from '@/components/common/Toggle.vue'
  import { nanoid } from 'nanoid'

  interface State {
    wallName: string
    wallDescription: string
    canvasCourseID: string

    searchSubject: string
    newSubject: string
    selectedEmoji: string

    confirmationPopupIsVisible: boolean
    duplicationPopupIsVisible: boolean
    subjectDropdownIsVisible: boolean
    emojiDropdownIsVisible: boolean

    subjects: Subject[]
    allSubjects: Subject[]
    tasks: Task[]
    taskTitles: string[]
    selectedTask?: Task

    submittedParcelActions: ParcelType[]
  }

  const common = useCommonStore()
  const colorStore = useColorStore()
  const wallStore = useWallStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const parcelStore = useParcelStore()
  const dialogStore = useDialogStore()
  const router = useRouter()
  const route = useRoute()

  const { t } = useI18n({
  messages: {
    en: {
      taskHasContent: 'Task has content!',
    },
    no: {
      taskHasContent: 'Oppgaven har innhold!',
      },
   },
  })

  const emit = defineEmits<{
    (e: 'closePopup', change: number, persist: boolean): void
    (e: 'setTaskId', id: string): void
  }>()

  const props = defineProps({
    selectedTaskIndex: { type: Number, default: 0 },
    wall: { type: Object as PropType<Wall | undefined>, default: undefined }, // might not have a child
  })

  const state = ref<State>({
    wallName: t('name'),
    wallDescription: t('description'),
    canvasCourseID: '',

    searchSubject: '',
    newSubject: '',
    selectedEmoji: '',

    confirmationPopupIsVisible: false,
    duplicationPopupIsVisible: false,
    subjectDropdownIsVisible: false,
    emojiDropdownIsVisible: false,

    subjects: [],
    allSubjects: [],
    tasks: [],
    taskTitles: [],
    selectedTask: undefined,

    submittedParcelActions: [],
  })

  const user = userStore.getters.user
  const firstFocus = ref()

  async function assembleNewWall() {
    const u = user.value
    if (u) {
      const wallSpec: IWall = {
        details: {
          title: state.value.wallName,
          description: state.value.wallDescription,
          canvasCourseID: state.value.canvasCourseID,
          subjects: state.value.subjects,
        },
        users: {
          owner: {
            type: u.status.isRegistered ? CreatorType.REGISTERED : CreatorType.ANONYMOUS,
            username: u.profile.username,
            user_id: u._id,
          },
        },
        tasks: state.value.tasks,
      }
      const wall = new Wall(wallSpec)
      const res = await wallStore.actions.createWall(wall, false)
      if (res.success) {
        if (state.value.canvasCourseID != '' && wallStore.getters.selectedWall.value) {
          const sharing = new Sharing({
            _id: '',
            created: new Date(),
            access: true,
            group_id: state.value.canvasCourseID,
            groupType: GroupType.CANVAS,
            permissionLevel: PermissionLevel.Join,
          })

          const res = await wallStore.actions.createShare(wallStore.getters.selectedWall.value._id, sharing)
          if (!res.success) dialogStore.actions.pushMessage('', DialogMessageType.Error, t('nousersingroup'), 5000)
        }
      } else if (!res.success || res.error) {
        // catch error -> dialog or message to the user
      }
    }
  }

  onBeforeMount(() => {
    state.value.canvasCourseID = (router.currentRoute.value.params.wallsearchvalue as string) ?? ''
  })

  onMounted(async () => {
    const res = await wallStore.actions.getSubjects()
    if (res.success && res.body) state.value.allSubjects = res.body as Subject[]
    if (props.wall) {
      state.value.wallName = props.wall.details.title
      state.value.wallDescription = props.wall.details.description
      props.wall.tasks.map((t) => state.value.tasks.push({ ...t, status: { ...t.status } }))
      state.value.selectedTask = state.value.tasks[props.selectedTaskIndex]
      sortTasks()
      props.wall.details.subjects.forEach((s) => state.value.subjects.push({ ...s }))
    }
    nextTick(() => {
      firstFocus.value?.focus()
    })

    //Add a single default task but for new walls only
    if (!props.wall && state.value.tasks.length === 0) {
      addTask()
    }
  })


  const hover = ref<{ show: boolean; top: number; left: number; index: number }>({
  show: false,
  top: 0,
  left: 0,
  index: -1,
})

function showTaskTooltip(e: MouseEvent, idx: number) {
  const el = e.currentTarget as HTMLElement
  const rect = el.getBoundingClientRect()
  const tooltipWidth = 200 // rough width; adjust if needed
  const gap = 8
  hover.value = {
    show: true,
    index: idx,

    //Center position on the task
    top: rect.top + window.scrollY + rect.height / 2 - 10,

    //No room to the right of the button, so use available horizontaal space to the buttons left side
    left: rect.left + window.scrollX - tooltipWidth - gap, 
  }
}

function hideTaskTooltip() {
  hover.value.show = false
}


  const pageIsDashboard = computed(() => {
    return route.fullPath.includes('dashboard')
  })

  const filteredSubjects = computed(() => {
    return state.value.searchSubject.length > 0 && state.value.searchSubject !== t('add')
      ? state.value.allSubjects.filter((s: Subject) => s.name.toLowerCase().includes(state.value.searchSubject.toLowerCase()))
      : state.value.subjects
  })

  async function saveAndClose(duplicate: boolean) {
    if (props.wall) {
      await updateWall(duplicate)
    } else {
      await assembleNewWall()
      router.push({ name: 'Wall', query: { id: wallStore.getters.selectedWall.value?._id } })
    }
    emit(
      'closePopup',
      state.value.tasks.findIndex((t) => t._id === state.value.selectedTask?._id),
      true,
    )
  }

  function close() {
    emit('closePopup', 0, false)
  }

  async function deleteWall() {
    if (props.wall) await wallStore.actions.deleteWall(props.wall._id)
  }

  function removeDefaultText() {
    if (state.value.wallDescription === t('description')) state.value.wallDescription = ''
  }

  function removeTaskDefaultText(task: Task) {
    if (task.title === t('defaulttask')) task.title = ''
  }

  function sortTasks() {
    state.value.tasks = state.value.tasks.filter((t) => !t.status.deleted)
    state.value.tasks.sort((c: Task, p: Task) => {
      return c.order < p.order ? -1 : 1
    })
  }

  function submitTaskEdit(index: number, target: EventTarget | null) {
    if (target) {
      state.value.taskTitles[index] = (target as HTMLInputElement).value
      state.value.tasks[index].title = state.value.taskTitles[index]
      state.value.submittedParcelActions.push(ParcelType.TaskEdited)
    }
  }

  function submitTaskOrderChange(index: number, change: number) {
    state.value.tasks[index].order = index + change
    state.value.tasks[index + change].order = index

    const switchedTask = state.value.taskTitles[index]
    state.value.taskTitles[index] = state.value.taskTitles[index + change]
    state.value.taskTitles[index + change] = switchedTask

    sortTasks()
    state.value.submittedParcelActions.push(ParcelType.TaskEdited)
  }

  function submitSubjectRemoval(subject: Subject) {
    const index = state.value.subjects.findIndex((s) => s._id === subject._id)
    state.value.subjects.splice(index, 1)
  }

  function addSubject(subject: Subject) {
    const subjectIndex = state.value.subjects.findIndex((s) => s._id === subject._id)
    if (subjectIndex === -1) state.value.subjects.push(subject)
  }

  function submitTaskDeletion(index: number) {
    if (taskDeletionIsAllowed(index)) {
      // only allow delete if no notes have been added
      if (state.value.tasks.length - 1 > 0) {
        state.value.tasks[index].status.deleted = true
        sortTasks()
        const newTaskIndex = state.value.tasks.length > 0 && index > 0 ? index - 1 : 0
        const newTaskID = state.value.tasks.length > 0 ? state.value.tasks[newTaskIndex]._id : ''
        if (newTaskID) emit('setTaskId', newTaskID)
      } else dialogStore.actions.pushMessage('Error', DialogMessageType.Error, t('taskdeletion'))
      state.value.submittedParcelActions.push(ParcelType.TaskDelete)
    }
  }

  function taskDeletionIsAllowed(index: number) {
    let found = false
    for (const note of noteStore.getters.sortedNotes.value) if (note.task_id === state.value.tasks[index]._id) found = true
    return !found
  }

  async function updateWall(duplicate: boolean) {
    //Preserve wall id on updates, get it from the store instead of creating a new wall object
    let wall = wallStore.getters.selectedWall.value
    if (!wall || !wall._id) {
      return false
    }

    //let wall = new Wall(props.wall)
    wall.details.title = state.value.wallName
    wall.details.description = state.value.wallDescription
    wall.tasks = state.value.tasks // save task changes - not persisted if submit button was not clicked
    wall.details.subjects = state.value.subjects // selected subjects
    for (let i = 0; i < state.value.tasks.length; i++) wall.tasks[i].title = state.value.taskTitles[i] || wall.tasks[i].title
    if (!duplicate) {
      // regular save
      const res = await wallStore.actions.updateWall(wall)

      //New attempt to preserve wall id
      if (res.success && wall._id) {
        const refresh = await wallStore.actions.getWall(wall._id)
        const updatedWall = wallStore.getters.selectedWall.value

        if (!updatedWall) {
          console.log('Updated wall not found')
          return false
        }

        state.value.tasks = updatedWall.tasks
        state.value.wallName = updatedWall.details.title
        state.value.wallDescription = updatedWall.details.description
        state.value.subjects = updatedWall.details.subjects

        for (let action of state.value.submittedParcelActions) {
          let currentBody
          switch (action) {
            case ParcelType.TaskCreate:
            case ParcelType.TaskEdited:
            case ParcelType.TaskDelete:
              currentBody = updatedWall.tasks
              break
            case ParcelType.WallEdited:
              currentBody = updatedWall
              break
          }
          const parcel = new Parcel({ parcelType: action, body: currentBody }, user.value, updatedWall)
          await parcelStore.actions.sendParcel(updatedWall._id, parcel)
        }

        return true
      }
    } else if (user.value) {
      // duplicate from popup
      wall.prepareDuplication(t('copyOf'))
      wall.assignNewOwner(user.value)
      try {
        const res = await wallStore.actions.createWall(wall, true)
        if (res.success && res.newWallId) {
          state.value.tasks = wall.tasks
          for (let note of noteStore.getters.sortedNotes.value) {
            let newNote = new Note(note)
            let newTask = getOriginalTaskByNote(newNote, res.newWallId)
            if (newTask) {
              newNote.task_id = newTask._id
              newNote.wall_id = res.newWallId // assign new note - wall relation
              for (let [key, entry] of Object.entries(note.entries)) {
                let convertedEntry = entry as BoardEntry
                const newBoardKey = getNewBoardId(key, res.newWallId)
                if (newBoardKey) {
                  convertedEntry.wall_id = newBoardKey
                  newNote.addBoardEntry(newBoardKey, convertedEntry)
                }
              }
            }
            await noteStore.actions.addNewNote(newNote, true)
          }
          return true
        }
      } catch (e) {
        dialogStore.actions.pushMessage('Error', DialogMessageType.Error, t('wallduplication'))
      }
    }
    await wallStore.actions.getUserWalls('') // force state walls update
    state.value.submittedParcelActions = []
  }

  function getOriginalTaskByNote(note: Note, newWallId: string) {
    let task = props.wall?.tasks.find((t: Task) => t._id === note.task_id) // original task
    if (task) return getDuplicatedTaskByName(task.order, newWallId)
  }

  function getNewBoardId(originalBoardId: string, newWallId: string) {
    let originalBoardName = props.wall?.boards.find((b: Board) => b._id === originalBoardId)?.name
    if (originalBoardName) {
      let newBoard = wallStore.getters.allWalls.value.find((w: Wall) => w._id === newWallId)?.boards.find((b: Board) => b.name === originalBoardName)
      if (newBoard && newBoard._id) return newBoard._id
    }
  }

  function getDuplicatedTaskByName(order: number, id: string) {
    return wallStore.getters.allWalls.value.find((w: Wall) => w._id === id)?.tasks.find((t: Task) => t.order === order)
  }

  function addTask() {
    const index = state.value.tasks.length - 1
    state.value.tasks.push({
      _id: nanoid(),
      title: t('defaulttask'),
      status: {
        created: new Date(),
        deleted: false,
      },
      order: index + 1,
      allowBoardCreation: true,
    })
    // For existing walls only
    if (props.wall) state.value.submittedParcelActions.push(ParcelType.TaskCreate)
  }

  function toggleBoardCreation(index: number, value: boolean) {
    const updatedTasks = [...state.value.tasks]
    updatedTasks[index].allowBoardCreation = value

    state.value.tasks = updatedTasks
    wallStore.actions.updateTasks(updatedTasks)
  }

  //function toggleBoardCreation(index: number, value: boolean) {
  //  state.tasks[index].allowBoardCreation = value
  //}

  function chooseEmoji(emoji: string) {
    state.value.selectedEmoji = emoji
    toggleEmojiDropdown()
  }

  function confirmNewSubject() {
    if (state.value.newSubject && state.value.selectedEmoji) {
      const subject = {
        name: state.value.newSubject,
        icon: state.value.selectedEmoji,
      } as Subject

      addSubject(subject)
      state.value.selectedEmoji = ''
      state.value.newSubject = ''
    }
  }

  function toggleEmojiDropdown() {
    state.value.emojiDropdownIsVisible = !state.value.emojiDropdownIsVisible
  }

  function subjectWrapperClass() {
    return state.value.emojiDropdownIsVisible ? 'pb-5' : ''
  }

  function deleteButtonStyles(isVisible: boolean) {
    return isVisible ? '' : 'opacity-25 cursor-not-allowed'
  }

  function subjectIsSelected(subject: Subject) {
    return state.value.subjects.findIndex((s) => s._id === subject._id) > -1
  }

  // POPUP VISIBILITY:

  function invokeDeletionPopup() {
    state.value.confirmationPopupIsVisible = true
  }

  function closeDeletionPopup() {
    state.value.confirmationPopupIsVisible = false
  }

  function invokeDuplicationPopup() {
    state.value.duplicationPopupIsVisible = true
  }

  function closeDuplicationPopup() {
    state.value.duplicationPopupIsVisible = false
  }

  function setDropdownState() {
    state.value.subjectDropdownIsVisible = true
  }
</script>

<style scoped></style>
